import type { SortState } from "../../util/listFilters"
import type { CityGenericItem } from "../../util/cityOptionsUtil"
import type { IGetQuotationListApiParams, IGetQuotationListApiSortBy } from "../../models/axiosModel"

export const QUOTATION_LIST_FILTER_KEYS = {
    city: "city",
    status: "status",
} as const

/** Search waits this long after the last keystroke before hitting the API. */
export const QUOTATION_LIST_SEARCH_DEBOUNCE_MS = 400

/** Table column key → backend `sort_by` value. */
export const QUOTATION_LIST_SORT_BY_COLUMN: Record<string, IGetQuotationListApiSortBy> = {
    sta_code: "sta_code",
    source: "source",
    quotation_name: "quotation_name",
    agency_name: "agency_name",
    cities_covered: "destination",
    travel_dates: "start_date",
    total_pax: "pax",
    total_price_usd: "price",
    status: "status",
    created_by: "created_by",
}

const joinIds = (values: string[] | undefined): string | undefined => {
    const ids = (values || []).map((v) => String(v).trim()).filter(Boolean)
    return ids.length > 0 ? ids.join(",") : undefined
}

export interface IQuotationListQueryInput {
    page: number
    pageSize: number
    search: string
    filters: Record<string, string[]>
    sortState: SortState
}

/** Unsorted / unknown column → omit sort params so backend default (sta_code desc) applies. */
export const buildQuotationListApiParams = ({
    page,
    pageSize,
    search,
    filters,
    sortState,
}: IQuotationListQueryInput): IGetQuotationListApiParams => {
    const sortBy = sortState.key ? QUOTATION_LIST_SORT_BY_COLUMN[sortState.key] : undefined
    const hasSort = Boolean(sortBy && sortState.direction)
    const trimmedSearch = search.trim().slice(0, 100)

    return {
        page: Math.max(1, page),
        limit: pageSize,
        search: trimmedSearch || undefined,
        quotation_status_id: joinIds(filters[QUOTATION_LIST_FILTER_KEYS.status]),
        city_id: joinIds(filters[QUOTATION_LIST_FILTER_KEYS.city]),
        sort_by: hasSort ? sortBy : undefined,
        sort_order: hasSort ? sortState.direction ?? undefined : undefined,
    }
}

/** All cities (inactive too — old quotations may still cover them), sorted by name. */
export const buildQuotationListCityFilterOptions = (
    cities: CityGenericItem[] | undefined | null,
): { label: string; value: string }[] =>
    (cities ?? [])
        .filter((city) => city?.city_id != null && String(city.city_name || "").trim())
        .map((city) => ({
            value: String(city.city_id),
            label: city.flag_active === false
                ? `${String(city.city_name).trim()} (Inactive)`
                : String(city.city_name).trim(),
        }))
        .sort((a, b) => a.label.localeCompare(b.label))

/** Page to jump back to when the current page is past the end (e.g. last row on last page deleted). */
export const resolveQuotationListOverflowPage = (page: number, total: number, pageSize: number): number | null => {
    if (total <= 0 || pageSize <= 0) return null
    const lastPage = Math.ceil(total / pageSize)
    return page > lastPage ? lastPage : null
}
